import React, { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import { formatDate } from '@/utils/format';

const EssOtPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.essOtRequests(),
    queryFn: () => hrmsService.listEssOtRequests(),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: hrmsKeys.essOtRequests() });
    queryClient.invalidateQueries({ queryKey: hrmsKeys.essSummary() });
  };

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => hrmsService.createEssOtRequest(body),
    onSuccess: (_d, vars) => {
      message.success(vars.submit ? 'OT submitted for approval' : 'OT saved as draft');
      form.resetFields();
      form.setFieldsValue({ ot_kind: 'Normal' });
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'OT save failed'),
  });

  const submitMut = useMutation({
    mutationFn: (id: string) => hrmsService.submitEssOtRequest(id),
    onSuccess: () => {
      message.success('OT submitted for approval');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Submit failed'),
  });

  const save = async (submit: boolean) => {
    const v = await form.validateFields();
    createMut.mutate({
      ot_date: v.ot_date.format('YYYY-MM-DD'),
      requested_hours: v.requested_hours,
      ot_kind: v.ot_kind || 'Normal',
      reason: v.reason,
      submit,
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Date',
      dataIndex: 'ot_date',
      width: 120,
      render: (d: string) => (d ? formatDate(d) : '—'),
    },
    { title: 'Hours', dataIndex: 'requested_hours', width: 90 },
    { title: 'Kind', dataIndex: 'ot_kind', width: 100, render: (v) => v || 'Normal' },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true, render: (v, r) => v || r.notes || '—' },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, r) =>
        String(r.status) === 'Draft' || String(r.status) === 'Rejected' ? (
          <Button size="small" type="link" loading={submitMut.isPending} onClick={() => submitMut.mutate(r.id)}>
            Submit for approval
          </Button>
        ) : null,
    },
  ];

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="My overtime"
          subtitle="Request overtime — save draft or submit for approval"
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="OT is saved as Draft unless you choose Submit for approval."
      />
      <Card title="Request overtime" size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" initialValues={{ ot_kind: 'Normal' }}>
          <Form.Item name="ot_date" label="OT date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="requested_hours" label="Hours" rules={[{ required: true }]}>
            <InputNumber min={0.5} max={24} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ot_kind" label="Kind">
            <Select options={['Normal', 'Friday', 'Holiday', 'Night'].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Space>
            <Button loading={createMut.isPending} onClick={() => save(false).catch(() => undefined)}>
              Save as draft
            </Button>
            <Button type="primary" loading={createMut.isPending} onClick={() => save(true).catch(() => undefined)}>
              Submit for approval
            </Button>
          </Space>
        </Form>
      </Card>

      <Card title="My OT requests" size="small">
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 15 }}
        />
      </Card>
    </WorkspaceLayout>
  );
};

export default EssOtPage;
